import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import Autocomplete from '@mui/material/Autocomplete';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { alpha, styled } from '@mui/material/styles';
import { WalletCards, Calculator, Search, User, Phone, FileDown, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LoaderProgress from '../components/ui/loader-progress';
import api from '../services/api';
import { useJob } from '../context/JobContext';
import { toast } from 'react-toastify';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const BASE_FILTERS = {
  search: '',
  responsabileLegale: [],
  controparte: [],
  tipologia: [],
  status: ['closed'],
  modalitaChiusura: [],
  importoAccordatoFilter: '',
  dataAssegnazioneFrom: '',
  dataAssegnazioneTo: '',
  dataChiusuraFrom: '',
  dataChiusuraTo: ''
};

const IMPORTO_ACCORDATO_FILTER_OPTIONS = [
  { value: 'eq0', label: '= 0' },
  { value: 'gt0', label: '> 0' },
  { value: 'lt0', label: '< 0' }
];

const loadDefaultFilters = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const today = `${year}-${month}-${day}`;
  const monthStart = `${year}-${month}-01`;
  return {
    ...BASE_FILTERS,
    dataChiusuraFrom: monthStart,
    dataChiusuraTo: today
  };
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatMoney = (value) => {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(n);
};

const formatPercent = (value) => {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '-';
  return `${n.toFixed(1)}%`;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const buildLegaliFiltersParams = (filtersArg) => ({
  search: filtersArg.search || '',
  responsabileLegale: toArray(filtersArg.responsabileLegale).join(','),
  controparte: toArray(filtersArg.controparte).join(','),
  tipologia: toArray(filtersArg.tipologia).join(','),
  status: toArray(filtersArg.status).join(','),
  modalitaChiusura: toArray(filtersArg.modalitaChiusura).join(','),
  importoAccordatoFilter: String(filtersArg.importoAccordatoFilter || '').trim(),
  dataAssegnazioneFrom: filtersArg.dataAssegnazioneFrom || '',
  dataAssegnazioneTo: filtersArg.dataAssegnazioneTo || '',
  dataChiusuraFrom: filtersArg.dataChiusuraFrom || '',
  dataChiusuraTo: filtersArg.dataChiusuraTo || ''
});

const getImportoAccordatoFilterLabel = (value) => {
  const match = IMPORTO_ACCORDATO_FILTER_OPTIONS.find((option) => option.value === value);
  return match ? match.label : '';
};

const resolveTrendMonth = (filtersArg) => {
  const candidate =
    String(filtersArg?.dataChiusuraTo || '').trim() ||
    String(filtersArg?.dataChiusuraFrom || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) return candidate.slice(0, 7);
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftIsoDateByMonths = (isoDate, monthsDelta) => {
  if (!isoDate) return '';
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  const originalDay = parsed.getDate();
  parsed.setDate(1);
  parsed.setMonth(parsed.getMonth() + monthsDelta);
  const lastDayOfTargetMonth = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0).getDate();
  parsed.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getCustomerDisplayName = (row) => {
  if (!row) return '-';
  if (row.customer_ragione_sociale) return row.customer_ragione_sociale;
  const fullName = [row.customer_name, row.customer_surname]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ');
  return fullName || '-';
};

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={(theme) => ({
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0',
      borderRadius: 2,
      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.78) : '#ffffff'
    })}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function LegaliChiusure() {
  const { addJob } = useJob();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    totalePratiche: 0,
    totaleRisparmiatoEuro: 0,
    totaleRisparmiatoPercentuale: 0
  });
  const [comparisonStats, setComparisonStats] = useState({
    previousTotal: 0,
    deltaPct: 0,
    deltaAbs: 0
  });
  const [distribution, setDistribution] = useState([]);
  const [issuerDistribution, setIssuerDistribution] = useState([]);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState({ trendMonth: '', data: [] });
  const [yearComparison, setYearComparison] = useState({
    currentYear: new Date().getFullYear(),
    previousYear: new Date().getFullYear() - 1,
    data: []
  });
  const [viewTab, setViewTab] = useState('list');
  const [orderBy, setOrderBy] = useState('data_chiusura');
  const [order, setOrder] = useState('desc');
  const [filters, setFilters] = useState(BASE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(BASE_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [includeReportDetail, setIncludeReportDetail] = useState(true);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [options, setOptions] = useState({
    responsabiliLegali: [],
    controparti: [],
    tipologie: [],
    statuses: [],
    modalitaChiusura: []
  });
  const [columnVisibility, setColumnVisibility] = useState({
    responsabileLegale: true,
    dataAssegnazione: true,
    cliente: true,
    controparte: true,
    tipologia: true,
    dataChiusura: true,
    giorniChiusura: true,
    importoChiusura: true,
    importoAccordato: true,
    percentualeRisparmio: true,
    modalitaChiusura: true
  });

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get('/negotiation/legali-chiusure/filters');
      setOptions({
        responsabiliLegali: Array.isArray(res.data?.responsabiliLegali) ? res.data.responsabiliLegali : [],
        controparti: Array.isArray(res.data?.controparti) ? res.data.controparti : [],
        tipologie: Array.isArray(res.data?.tipologie) ? res.data.tipologie : [],
        statuses: Array.isArray(res.data?.statuses) ? res.data.statuses : [],
        modalitaChiusura: Array.isArray(res.data?.modalitaChiusura) ? res.data.modalitaChiusura : []
      });
    } catch (error) {
      console.error('Errore caricamento opzioni filtri legali chiusure:', error);
      setOptions({
        responsabiliLegali: [],
        controparti: [],
        tipologie: [],
        statuses: [],
        modalitaChiusura: []
      });
    }
  };

  const fetchRows = async (
    pageArg = page,
    rowsArg = rowsPerPage,
    orderByArg = orderBy,
    orderArg = order,
    filtersArg = appliedFilters
  ) => {
    setLoading(true);
    try {
      const baseFiltersParams = buildLegaliFiltersParams(filtersArg);
      const trendMonth = resolveTrendMonth(filtersArg);
      const referenceYear = Number(String(trendMonth).slice(0, 4)) || new Date().getFullYear();
      const res = await api.get('/negotiation/legali-chiusure', {
        params: {
          page: pageArg + 1,
          limit: rowsArg,
          orderBy: orderByArg,
          order: orderArg,
          ...baseFiltersParams
        }
      });
      const currentTotal = Number(res.data?.stats?.totalePratiche || 0);
      const previousFilters = {
        ...filtersArg,
        dataChiusuraFrom: shiftIsoDateByMonths(filtersArg.dataChiusuraFrom, -1),
        dataChiusuraTo: shiftIsoDateByMonths(filtersArg.dataChiusuraTo, -1)
      };
      let previousTotal = 0;
      try {
        const prevRes = await api.get('/negotiation/legali-chiusure', {
          params: {
            page: 1,
            limit: 1,
            orderBy: orderByArg,
            order: orderArg,
            ...buildLegaliFiltersParams(previousFilters)
          }
        });
        previousTotal = Number(prevRes.data?.stats?.totalePratiche || 0);
      } catch (prevError) {
        console.error('Errore caricamento confronto mese precedente legali:', prevError);
      }
      const deltaAbs = currentTotal - previousTotal;
      const deltaPct = previousTotal === 0 ? (currentTotal > 0 ? 100 : 0) : (deltaAbs / previousTotal) * 100;

      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.total || 0));
      setStats({
        totalePratiche: currentTotal,
        totaleRisparmiatoEuro: Number(res.data?.stats?.totaleRisparmiatoEuro || 0),
        totaleRisparmiatoPercentuale: Number(res.data?.stats?.totaleRisparmiatoPercentuale || 0)
      });
      setComparisonStats({
        previousTotal,
        deltaPct,
        deltaAbs
      });

      const [distributionResult, issuerDistributionResult, categoryDistributionResult, trendResult, yearComparisonResult] = await Promise.allSettled([
        api.get('/negotiation/legali-chiusure/distribution', { params: baseFiltersParams }),
        api.get('/negotiation/legali-chiusure/distribution-issuer', { params: baseFiltersParams }),
        api.get('/negotiation/legali-chiusure/distribution-category', { params: baseFiltersParams }),
        api.get('/negotiation/legali-chiusure/trend-monthly', {
          params: {
            ...baseFiltersParams,
            trendMonth
          }
        }),
        api.get('/negotiation/legali-chiusure/year-comparison', {
          params: {
            ...baseFiltersParams,
            referenceYear
          }
        })
      ]);

      if (distributionResult.status === 'fulfilled') {
        setDistribution(
          Array.isArray(distributionResult.value.data?.distribution)
            ? distributionResult.value.data.distribution
            : []
        );
      } else {
        setDistribution([]);
      }

      if (issuerDistributionResult.status === 'fulfilled') {
        setIssuerDistribution(
          Array.isArray(issuerDistributionResult.value.data?.distribution)
            ? issuerDistributionResult.value.data.distribution
            : []
        );
      } else {
        setIssuerDistribution([]);
      }

      if (categoryDistributionResult.status === 'fulfilled') {
        setCategoryDistribution(
          Array.isArray(categoryDistributionResult.value.data?.distribution)
            ? categoryDistributionResult.value.data.distribution
            : []
        );
      } else {
        setCategoryDistribution([]);
      }

      if (trendResult.status === 'fulfilled') {
        setMonthlyTrend({
          trendMonth: String(trendResult.value.data?.trendMonth || trendMonth),
          data: Array.isArray(trendResult.value.data?.data) ? trendResult.value.data.data : []
        });
      } else {
        setMonthlyTrend({ trendMonth, data: [] });
      }

      if (yearComparisonResult.status === 'fulfilled') {
        setYearComparison({
          currentYear: Number(yearComparisonResult.value.data?.currentYear || referenceYear),
          previousYear: Number(yearComparisonResult.value.data?.previousYear || referenceYear - 1),
          data: Array.isArray(yearComparisonResult.value.data?.data)
            ? yearComparisonResult.value.data.data
            : []
        });
      } else {
        setYearComparison({
          currentYear: referenceYear,
          previousYear: referenceYear - 1,
          data: []
        });
      }
    } catch (error) {
      console.error('Errore caricamento legali chiusure:', error);
      setRows([]);
      setTotal(0);
      setStats({
        totalePratiche: 0,
        totaleRisparmiatoEuro: 0,
        totaleRisparmiatoPercentuale: 0
      });
      setComparisonStats({
        previousTotal: 0,
        deltaPct: 0,
        deltaAbs: 0
      });
      setDistribution([]);
      setIssuerDistribution([]);
      setCategoryDistribution([]);
      setMonthlyTrend({ trendMonth: resolveTrendMonth(filtersArg), data: [] });
      setYearComparison({
        currentYear: new Date().getFullYear(),
        previousYear: new Date().getFullYear() - 1,
        data: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadedDefaults = loadDefaultFilters();
    setFilters(loadedDefaults);
    setAppliedFilters(loadedDefaults);
    setSearchInput(loadedDefaults.search || '');
    fetchFilterOptions();
    fetchRows(0, rowsPerPage, orderBy, order, loadedDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === filters.search) return;
      const next = { ...filters, search: searchInput };
      setFilters(next);
      setAppliedFilters(next);
      setPage(0);
      fetchRows(0, rowsPerPage, orderBy, order, next);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, filters, rowsPerPage, orderBy, order]);

  const applyFilters = (nextFilters) => {
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(0);
    fetchRows(0, rowsPerPage, orderBy, order, nextFilters);
  };

  const handleChangePage = (_event, nextPage) => {
    setPage(nextPage);
    fetchRows(nextPage, rowsPerPage, orderBy, order, appliedFilters);
  };

  const handleChangeRowsPerPage = (event) => {
    const nextRows = parseInt(event.target.value, 10) || 25;
    setRowsPerPage(nextRows);
    setPage(0);
    fetchRows(0, nextRows, orderBy, order, appliedFilters);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const nextOrder = isAsc ? 'desc' : 'asc';
    setOrder(nextOrder);
    setOrderBy(property);
    setPage(0);
    fetchRows(0, rowsPerPage, property, nextOrder, appliedFilters);
  };

  const activeFilters = useMemo(() => {
    const chips = [];
    if (toArray(appliedFilters.responsabileLegale).length > 0) {
      chips.push({
        key: 'responsabileLegale',
        label: `Resp. legale: ${toArray(appliedFilters.responsabileLegale).join(', ')}`
      });
    }
    if (toArray(appliedFilters.controparte).length > 0) {
      chips.push({
        key: 'controparte',
        label: `Controparte: ${toArray(appliedFilters.controparte).join(', ')}`
      });
    }
    if (toArray(appliedFilters.tipologia).length > 0) {
      chips.push({
        key: 'tipologia',
        label: `Tipologia: ${toArray(appliedFilters.tipologia).join(', ')}`
      });
    }
    if (toArray(appliedFilters.status).length > 0) {
      chips.push({
        key: 'status',
        label: `Status: ${toArray(appliedFilters.status).join(', ')}`
      });
    }
    if (toArray(appliedFilters.modalitaChiusura).length > 0) {
      chips.push({
        key: 'modalitaChiusura',
        label: `Modalità: ${toArray(appliedFilters.modalitaChiusura).join(', ')}`
      });
    }
    if (appliedFilters.importoAccordatoFilter) {
      chips.push({
        key: 'importoAccordatoFilter',
        label: `Importo accordato: ${getImportoAccordatoFilterLabel(appliedFilters.importoAccordatoFilter)}`
      });
    }
    if (appliedFilters.dataAssegnazioneFrom) {
      chips.push({
        key: 'dataAssegnazioneFrom',
        label: `Assegnazione dal: ${formatDate(appliedFilters.dataAssegnazioneFrom)}`
      });
    }
    if (appliedFilters.dataAssegnazioneTo) {
      chips.push({
        key: 'dataAssegnazioneTo',
        label: `Assegnazione al: ${formatDate(appliedFilters.dataAssegnazioneTo)}`
      });
    }
    if (appliedFilters.dataChiusuraFrom) {
      chips.push({
        key: 'dataChiusuraFrom',
        label: `Chiusura dal: ${formatDate(appliedFilters.dataChiusuraFrom)}`
      });
    }
    if (appliedFilters.dataChiusuraTo) {
      chips.push({
        key: 'dataChiusuraTo',
        label: `Chiusura al: ${formatDate(appliedFilters.dataChiusuraTo)}`
      });
    }
    if (appliedFilters.search) {
      chips.push({ key: 'search', label: `Ricerca: ${appliedFilters.search}` });
    }
    return chips;
  }, [appliedFilters]);

  const handleDeleteActiveFilter = (key) => {
    const nextValue = ['responsabileLegale', 'controparte', 'tipologia', 'status', 'modalitaChiusura'].includes(key)
      ? []
      : '';
    const next = { ...appliedFilters, [key]: nextValue };
    setFilters(next);
    setAppliedFilters(next);
    if (key === 'search') setSearchInput('');
    setPage(0);
    fetchRows(0, rowsPerPage, orderBy, order, next);
  };

  const handleStartLegaliReport = async () => {
    try {
      if (reportLoading) return;
      setReportLoading(true);
      const payload = {
        includeDettaglioPratiche: includeReportDetail,
        filters: {
          search: appliedFilters.search || '',
          responsabileLegale: toArray(appliedFilters.responsabileLegale),
          controparte: toArray(appliedFilters.controparte),
          tipologia: toArray(appliedFilters.tipologia),
          status: toArray(appliedFilters.status),
          modalitaChiusura: toArray(appliedFilters.modalitaChiusura),
          importoAccordatoFilter: String(appliedFilters.importoAccordatoFilter || '').trim(),
          dataAssegnazioneFrom: appliedFilters.dataAssegnazioneFrom || '',
          dataAssegnazioneTo: appliedFilters.dataAssegnazioneTo || '',
          dataChiusuraFrom: appliedFilters.dataChiusuraFrom || '',
          dataChiusuraTo: appliedFilters.dataChiusuraTo || ''
        }
      };
      const res = await api.post('/reports/legali-chiusure', payload);
      const jobId = res?.data?.jobId;
      if (!jobId) throw new Error('jobId non ricevuto');
      addJob(jobId, 'legali_chiusure_report');
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Errore avvio report legali chiusure:', error);
      toast.error("Errore durante l'avvio del report legali chiusure");
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (csvLoading) return;
    setCsvLoading(true);
    try {
      const payload = {
        filters: {
          search: appliedFilters.search || '',
          responsabileLegale: toArray(appliedFilters.responsabileLegale),
          controparte: toArray(appliedFilters.controparte),
          tipologia: toArray(appliedFilters.tipologia),
          status: toArray(appliedFilters.status),
          modalitaChiusura: toArray(appliedFilters.modalitaChiusura),
          importoAccordatoFilter: String(appliedFilters.importoAccordatoFilter || '').trim(),
          dataAssegnazioneFrom: appliedFilters.dataAssegnazioneFrom || '',
          dataAssegnazioneTo: appliedFilters.dataAssegnazioneTo || '',
          dataChiusuraFrom: appliedFilters.dataChiusuraFrom || '',
          dataChiusuraTo: appliedFilters.dataChiusuraTo || ''
        }
      };
      const res = await api.post('/reports/legali-chiusure-csv', payload);
      const jobId = res?.data?.jobId;
      if (!jobId) throw new Error('jobId non ricevuto');
      addJob(jobId, 'legali_chiusure_csv_report');
    } catch (error) {
      console.error('Errore esportazione CSV legali chiusure:', error);
      toast.error("Errore durante l'avvio dell'esportazione CSV");
    } finally {
      setCsvLoading(false);
    }
  };

  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length;
  const totalColumns = Math.max(visibleColumnsCount, 1);
  const twoLineHeader = (top, bottom) => (
    <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.05 }}>
      <span>{top}</span>
      <span>{bottom}</span>
    </Box>
  );

  const distributionChartData = [...distribution]
    .map((row) => ({ name: row.name || 'Non assegnato', value: Number(row.total || 0) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
  const issuerDistributionChartData = [...issuerDistribution]
    .map((row) => ({ name: row.name || 'Non assegnata', value: Number(row.total || 0) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
  const categoryDistributionChartData = [...categoryDistribution]
    .map((row) => ({ name: row.name || 'Non assegnata', value: Number(row.total || 0) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);

  const trendPointMap = new Map(
    (monthlyTrend.data || [])
      .map((item) => [String(item?.day || ''), Number(item?.total || 0)])
      .filter(([day]) => /^\d{4}-\d{2}-\d{2}$/.test(day))
  );
  const trendMonthStr = String(monthlyTrend.trendMonth || '');
  const trendYear = Number(trendMonthStr.split('-')[0] || 0);
  const trendMonthNum = Number(trendMonthStr.split('-')[1] || 0);
  const hasValidTrendMonth =
    Number.isInteger(trendYear) &&
    Number.isInteger(trendMonthNum) &&
    trendYear > 0 &&
    trendMonthNum >= 1 &&
    trendMonthNum <= 12;
  const trendDaysInMonth = hasValidTrendMonth ? new Date(trendYear, trendMonthNum, 0).getDate() : 0;
  const monthlyTrendChartData = hasValidTrendMonth
    ? Array.from({ length: trendDaysInMonth }, (_unused, idx) => {
      const day = idx + 1;
      const key = `${trendYear}-${String(trendMonthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return { dayLabel: String(day).padStart(2, '0'), value: trendPointMap.get(key) || 0 };
    })
    : [];
  const monthlyTrendAverage =
    monthlyTrendChartData.length > 0
      ? monthlyTrendChartData.reduce((sum, item) => sum + Number(item.value || 0), 0) / monthlyTrendChartData.length
      : 0;
  const monthlyTrendChartDataWithAvg = monthlyTrendChartData.map((item) => ({ ...item, avg: monthlyTrendAverage }));
  const trendMonthLabel = hasValidTrendMonth
    ? new Date(trendYear, trendMonthNum - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
    : '';

  const monthLabelsShort = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  const yearComparisonChartData = Array.from({ length: 12 }, (_unused, idx) => {
    const month = idx + 1;
    const row = (yearComparison.data || []).find((item) => Number(item.month) === month);
    return {
      monthLabel: monthLabelsShort[idx],
      currentYearTotal: Number(row?.currentYearTotal || 0),
      previousYearTotal: Number(row?.previousYearTotal || 0)
    };
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: theme.palette.mode === 'dark' ? alpha('#6366f1', 0.2) : '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            })}
          >
            <FileText size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Legali (Chiusure)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1rem' }}>
              Elenco pratiche con dati legali e avanzamento chiusure
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FileDown size={16} />}
            onClick={handleExportCsv}
            disabled={loading || csvLoading}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {csvLoading ? 'Avvio...' : 'Esporta in CSV'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileText size={16} />}
            onClick={() => setReportDialogOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Report Chiusure
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <StatWidget
                title="Totale pratiche"
                value={stats.totalePratiche.toLocaleString('it-IT')}
                icon={FileText}
                color="#334155"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatWidget
                title="Totale risparmiato €"
                value={formatMoney(stats.totaleRisparmiatoEuro)}
                icon={WalletCards}
                color="#059669"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatWidget
                title="Totale risparmiato %"
                value={formatPercent(stats.totaleRisparmiatoPercentuale)}
                icon={Calculator}
                color="#0284c7"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={(theme) => ({
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.14)
                      : '#e2e8f0',
                  borderRadius: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.78)
                      : '#ffffff'
                })}
              >
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: '50%',
                    bgcolor:
                      comparisonStats.deltaAbs >= 0
                        ? alpha(theme.palette.success.main, 0.14)
                        : alpha(theme.palette.error.main, 0.14),
                    color: comparisonStats.deltaAbs >= 0 ? 'success.main' : 'error.main',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  })}
                >
                  {comparisonStats.deltaAbs >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Vs mese precedente
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: comparisonStats.deltaAbs >= 0 ? 'success.main' : 'error.main', lineHeight: 1.2 }}
                  >
                    {`${comparisonStats.deltaPct >= 0 ? '+' : ''}${comparisonStats.deltaPct.toFixed(1)}%`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${comparisonStats.deltaAbs >= 0 ? '+' : ''}${comparisonStats.deltaAbs} pratiche (mese prec: ${comparisonStats.previousTotal})`}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between'
          }}
        >
          <TextField
            size="small"
            placeholder="Cerca per cliente, controparte, responsabile..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="currentColor" />
                </InputAdornment>
              )
            }}
            sx={(theme) => ({
              minWidth: 360,
              '& .MuiInputAdornment-root': { color: theme.palette.text.secondary },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.42) : '#ffffff',
                fontSize: '1rem',
                color: theme.palette.text.primary,
                '& fieldset': { borderColor: theme.palette.divider },
                '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
              }
            })}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra filtri avanzati">
              <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)} sx={{ color: 'text.secondary' }}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Seleziona colonne">
              <IconButton size="small" onClick={(e) => setColumnMenuAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                <ViewColumnIcon fontSize="small" color={visibleColumnsCount < 11 ? 'primary' : 'action'} />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={columnMenuAnchorEl}
            open={Boolean(columnMenuAnchorEl)}
            onClose={() => setColumnMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Colonne visibili
              </Typography>
            </Box>
            <FormGroup sx={{ px: 2, pb: 1 }}>
              <FormControlLabel control={<Checkbox checked={columnVisibility.responsabileLegale} onChange={() => setColumnVisibility((prev) => ({ ...prev, responsabileLegale: !prev.responsabileLegale }))} />} label="Resp. legale" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.dataAssegnazione} onChange={() => setColumnVisibility((prev) => ({ ...prev, dataAssegnazione: !prev.dataAssegnazione }))} />} label="Data assegnazione" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.cliente} onChange={() => setColumnVisibility((prev) => ({ ...prev, cliente: !prev.cliente }))} />} label="Cliente" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.controparte} onChange={() => setColumnVisibility((prev) => ({ ...prev, controparte: !prev.controparte }))} />} label="Controparte" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.tipologia} onChange={() => setColumnVisibility((prev) => ({ ...prev, tipologia: !prev.tipologia }))} />} label="Tipologia" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.dataChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, dataChiusura: !prev.dataChiusura }))} />} label="Data chiusura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.giorniChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, giorniChiusura: !prev.giorniChiusura }))} />} label="GG chiusura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.importoChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, importoChiusura: !prev.importoChiusura }))} />} label="Importo chiusura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.importoAccordato} onChange={() => setColumnVisibility((prev) => ({ ...prev, importoAccordato: !prev.importoAccordato }))} />} label="Importo accordato" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.percentualeRisparmio} onChange={() => setColumnVisibility((prev) => ({ ...prev, percentualeRisparmio: !prev.percentualeRisparmio }))} />} label="% risparmio" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.modalitaChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, modalitaChiusura: !prev.modalitaChiusura }))} />} label="Modalità chiusura" />
            </FormGroup>
          </Menu>
        </Box>

        {showFilters && (
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={options.responsabiliLegali}
                  value={toArray(filters.responsabileLegale)}
                  onChange={(_event, value) => applyFilters({ ...filters, responsabileLegale: value })}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => <TextField {...params} label="Resp. legale" placeholder="Cerca..." />}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {option}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={options.controparti}
                  value={toArray(filters.controparte)}
                  onChange={(_event, value) => applyFilters({ ...filters, controparte: value })}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => <TextField {...params} label="Controparte" placeholder="Cerca..." />}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {option}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={options.tipologie}
                  value={toArray(filters.tipologia)}
                  onChange={(_event, value) => applyFilters({ ...filters, tipologia: value })}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => <TextField {...params} label="Tipologia" placeholder="Cerca..." />}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {option}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={options.statuses}
                  value={toArray(filters.status)}
                  onChange={(_event, value) => applyFilters({ ...filters, status: value })}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => <TextField {...params} label="Status" placeholder="Cerca..." />}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {option}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={options.modalitaChiusura}
                  value={toArray(filters.modalitaChiusura)}
                  onChange={(_event, value) => applyFilters({ ...filters, modalitaChiusura: value })}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => <TextField {...params} label="Modalità chiusura" placeholder="Cerca..." />}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {option}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  size="small"
                  options={IMPORTO_ACCORDATO_FILTER_OPTIONS}
                  value={IMPORTO_ACCORDATO_FILTER_OPTIONS.find((option) => option.value === filters.importoAccordatoFilter) || null}
                  onChange={(_event, option) =>
                    applyFilters({ ...filters, importoAccordatoFilter: option?.value || '' })
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option?.label || ''}
                  renderInput={(params) => <TextField {...params} label="Importo accordato" placeholder="Seleziona..." />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Assegnazione da"
                  value={filters.dataAssegnazioneFrom}
                  onChange={(e) => applyFilters({ ...filters, dataAssegnazioneFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Assegnazione a"
                  value={filters.dataAssegnazioneTo}
                  onChange={(e) => applyFilters({ ...filters, dataAssegnazioneTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Chiusura da"
                  value={filters.dataChiusuraFrom}
                  onChange={(e) => applyFilters({ ...filters, dataChiusuraFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Chiusura a"
                  value={filters.dataChiusuraTo}
                  onChange={(e) => applyFilters({ ...filters, dataChiusuraTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {activeFilters.length > 0 && (
          <Box
            sx={{
              px: 3,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 0.5 }}>
              Filtri attivi
            </Typography>
            {activeFilters.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={chip.label}
                onDelete={() => handleDeleteActiveFilter(chip.key)}
                sx={(theme) => ({
                  fontWeight: 600,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.24)' : '#F3F4F6',
                  color: theme.palette.text.primary,
                  border: '1px solid',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.45)'
                      : 'rgba(148, 163, 184, 0.35)'
                })}
              />
            ))}
          </Box>
        )}

        <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs value={viewTab} onChange={(_event, nextTab) => setViewTab(nextTab)} textColor="primary" indicatorColor="primary">
            <Tab value="list" label="Lista" />
            <Tab value="distribution" label="Distribuzione per legale" />
            <Tab value="distribution-issuer" label="Distribuzione per controparte" />
            <Tab value="distribution-category" label="Distribuzione per tipologia" />
            <Tab value="trend" label="Trend mensile" />
            <Tab value="year-comparison" label="Confronto annuale" />
          </Tabs>
        </Box>

        {viewTab === 'list' ? (
          <>
            <TableContainer>
              <Table
                sx={(theme) => ({
                  '& .MuiTableCell-root': {
                    borderColor: 'divider',
                    color: theme.palette.text.primary,
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }
                })}
              >
                <TableHead>
                  <TableRow>
                    {columnVisibility.responsabileLegale && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'responsabile_legale'} direction={orderBy === 'responsabile_legale' ? order : 'asc'} onClick={() => handleRequestSort('responsabile_legale')}>
                          {twoLineHeader('Resp.', 'legale')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.dataAssegnazione && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'data_assegnazione_legale'} direction={orderBy === 'data_assegnazione_legale' ? order : 'asc'} onClick={() => handleRequestSort('data_assegnazione_legale')}>
                          {twoLineHeader('Data assegn.', 'legale')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.cliente && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'cliente'} direction={orderBy === 'cliente' ? order : 'asc'} onClick={() => handleRequestSort('cliente')}>
                          Cliente
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.controparte && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'controparte'} direction={orderBy === 'controparte' ? order : 'asc'} onClick={() => handleRequestSort('controparte')}>
                          Controparte
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.tipologia && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'tipologia'} direction={orderBy === 'tipologia' ? order : 'asc'} onClick={() => handleRequestSort('tipologia')}>
                          Tipologia
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.dataChiusura && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'data_chiusura'} direction={orderBy === 'data_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('data_chiusura')}>
                          {twoLineHeader('Data', 'chiusura')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.giorniChiusura && (
                      <TableCell align="right">
                        <TableSortLabel active={orderBy === 'giorni_chiusura'} direction={orderBy === 'giorni_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('giorni_chiusura')}>
                          {twoLineHeader('GG', 'chiusura')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.importoChiusura && (
                      <TableCell align="right">
                        <TableSortLabel active={orderBy === 'importo_chiusura'} direction={orderBy === 'importo_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('importo_chiusura')}>
                          {twoLineHeader('Importo', 'chiusura')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.importoAccordato && (
                      <TableCell align="right">
                        <TableSortLabel active={orderBy === 'importo_accordato'} direction={orderBy === 'importo_accordato' ? order : 'asc'} onClick={() => handleRequestSort('importo_accordato')}>
                          {twoLineHeader('Importo', 'accordato')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.percentualeRisparmio && (
                      <TableCell align="right">
                        <TableSortLabel active={orderBy === 'percentuale_risparmio'} direction={orderBy === 'percentuale_risparmio' ? order : 'asc'} onClick={() => handleRequestSort('percentuale_risparmio')}>
                          {twoLineHeader('%', 'risparmio')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.modalitaChiusura && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'modalita_chiusura'} direction={orderBy === 'modalita_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('modalita_chiusura')}>
                          {twoLineHeader('Modalità', 'chiusura')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                        <LoaderProgress size={48} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                        Nessuna pratica trovata
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover sx={(theme) => ({ '&:hover td': { bgcolor: theme.palette.action.hover } })}>
                        {columnVisibility.responsabileLegale && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <User size={15} color="currentColor" />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {row.responsabile_legale_name || '-'}
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.dataAssegnazione && <TableCell>{formatDate(row.data_assegnazione_legale)}</TableCell>}
                        {columnVisibility.cliente && (
                          <TableCell>
                            <Box sx={{ minWidth: 260, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(37, 99, 235, 0.08)',
                                  color: '#2563eb',
                                  mt: 0.1,
                                  flexShrink: 0
                                }}
                              >
                                <User size={16} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    lineHeight: 1.15
                                  }}
                                >
                                  {getCustomerDisplayName(row)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                                  <Phone size={12} color="#64748b" />
                                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                    {row.customer_telephone || '-'}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.15, display: 'block', lineHeight: 1.15 }}>
                                  {row.customer_email || '-'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.controparte && <TableCell>{row.issuer_name || '-'}</TableCell>}
                        {columnVisibility.tipologia && <TableCell>{row.category_name || '-'}</TableCell>}
                        {columnVisibility.dataChiusura && <TableCell>{formatDate(row.data_chiusura)}</TableCell>}
                        {columnVisibility.giorniChiusura && (
                          <TableCell align="right">
                            {row.giorni_chiusura == null ? (
                              '-'
                            ) : (
                              <Chip
                                size="small"
                                label={`${Number(row.giorni_chiusura)} gg`}
                                sx={(theme) => {
                                  const giorni = Number(row.giorni_chiusura || 0);
                                  const tone =
                                    giorni <= 30
                                      ? theme.palette.success.main
                                      : giorni <= 60
                                        ? theme.palette.warning.main
                                        : theme.palette.error.main;
                                  return {
                                    height: 22,
                                    borderRadius: 999,
                                    fontWeight: 700,
                                    bgcolor: alpha(tone, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                    color: tone,
                                    border: '1px solid',
                                    borderColor: alpha(tone, 0.45),
                                    '& .MuiChip-label': { px: 1 }
                                  };
                                }}
                              />
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.importoChiusura && (
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                              {formatMoney(row.importo_chiusura)}
                            </Typography>
                          </TableCell>
                        )}
                        {columnVisibility.importoAccordato && (
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>
                              {formatMoney(row.importo_accordato)}
                            </Typography>
                          </TableCell>
                        )}
                        {columnVisibility.percentualeRisparmio && (
                          <TableCell align="right" sx={{ fontWeight: 700, color: Number(row.percentuale_risparmio || 0) >= 0 ? '#059669' : '#dc2626' }}>
                            {formatPercent(row.percentuale_risparmio)}
                          </TableCell>
                        )}
                        {columnVisibility.modalitaChiusura && <TableCell>{row.modalita_chiusura || '-'}</TableCell>}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Righe per pagina"
            />
          </>
        ) : viewTab === 'distribution' || viewTab === 'distribution-issuer' || viewTab === 'distribution-category' ? (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {viewTab === 'distribution'
                  ? 'Distribuzione per responsabile legale'
                  : viewTab === 'distribution-issuer'
                    ? 'Distribuzione per controparte'
                    : 'Distribuzione per tipologia'}
              </Typography>
              <Chip size="small" label="Filtri attivi" color="primary" variant="filled" />
            </Box>
            <Box
              sx={{
                height: Math.min(
                  Math.max(
                    280,
                    (viewTab === 'distribution'
                      ? distributionChartData.length
                      : viewTab === 'distribution-issuer'
                        ? issuerDistributionChartData.length
                        : categoryDistributionChartData.length) * 30 + 80
                  ),
                  900
                )
              }}
            >
              {loading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LoaderProgress size={48} />
                </Box>
              ) : (viewTab === 'distribution'
                ? distributionChartData
                : viewTab === 'distribution-issuer'
                  ? issuerDistributionChartData
                  : categoryDistributionChartData).length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  Nessun dato disponibile con i filtri correnti.
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      viewTab === 'distribution'
                        ? distributionChartData
                        : viewTab === 'distribution-issuer'
                          ? issuerDistributionChartData
                          : categoryDistributionChartData
                    }
                    layout="vertical"
                    margin={{ top: 10, right: 20, bottom: 10, left: 220 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={200} interval={0} />
                    <RechartsTooltip />
                    <Bar
                      dataKey="value"
                      fill={
                        viewTab === 'distribution'
                          ? '#6d28d9'
                          : viewTab === 'distribution-issuer'
                            ? '#2563eb'
                            : '#059669'
                      }
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>
        ) : viewTab === 'trend' ? (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Trend giornaliero mese {trendMonthLabel || '-'}
              </Typography>
              <Chip size="small" label={`Media giornaliera: ${monthlyTrendAverage.toFixed(1)}`} color="default" variant="outlined" />
            </Box>
            <Box sx={{ height: 320 }}>
              {loading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LoaderProgress size={48} />
                </Box>
              ) : monthlyTrendChartData.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  Nessun dato disponibile per il trend mensile.
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrendChartDataWithAvg} margin={{ top: 8, right: 20, bottom: 26, left: 24 }}>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#d1d5db" />
                    <XAxis dataKey="dayLabel" interval={0} tick={{ fontSize: 10, fill: '#6b7280' }} angle={-45} textAnchor="end" height={50} />
                    <YAxis allowDecimals={false} tick={{ fill: '#6b7280' }} />
                    <RechartsTooltip
                      formatter={(value, name) => [name === 'Media giornaliera' ? Number(value || 0).toFixed(1) : Number(value || 0), name]}
                      labelFormatter={(label) => `Giorno ${label}`}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="value" name="Totale pratiche" stroke="#22c55e" strokeWidth={4} dot={{ r: 4 }} />
                    <Line type="linear" dataKey="avg" name="Media giornaliera" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Confronto annuale {yearComparison.currentYear} vs {yearComparison.previousYear}
              </Typography>
              <Chip size="small" label={`${yearComparison.currentYear} vs ${yearComparison.previousYear}`} color="default" variant="outlined" />
            </Box>
            <Box sx={{ height: 340 }}>
              {loading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LoaderProgress size={48} />
                </Box>
              ) : yearComparisonChartData.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  Nessun dato disponibile per il confronto annuale.
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearComparisonChartData} margin={{ top: 12, right: 18, left: 10, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="monthLabel" />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip formatter={(value) => [value, 'Totale pratiche']} />
                    <Legend />
                    <Bar name={String(yearComparison.currentYear)} dataKey="currentYearTotal" fill="#22c55e" radius={[6, 6, 0, 0]} maxBarSize={24} />
                    <Bar name={String(yearComparison.previousYear)} dataKey="previousYearTotal" fill="#e11d48" radius={[6, 6, 0, 0]} maxBarSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>
        )}
      </StyledCard>

      <Dialog open={reportDialogOpen} onClose={() => !reportLoading && setReportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conferma generazione Report Chiusure</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Verrà generato un PDF asincrono con KPI, riepilogo per responsabile legale e dettaglio pratiche chiuse.
            Il file sarà disponibile nelle notifiche.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeReportDetail}
                onChange={(event) => setIncludeReportDetail(Boolean(event.target.checked))}
              />
            }
            label="Includi dettaglio pratiche chiuse"
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {activeFilters.length > 0 ? (
              activeFilters.map((chip) => (
                <Chip key={chip.key} size="small" label={chip.label} />
              ))
            ) : (
              <Chip size="small" label="Nessun filtro applicato" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>Annulla</Button>
          <Button onClick={handleStartLegaliReport} variant="contained" disabled={reportLoading}>
            {reportLoading ? 'Avvio...' : 'Genera PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
